import React from 'react';
import { Eye, MessageCircle, Edit } from 'lucide-react';
import styles from './ProjectItemCard.module.scss';
import '@github/relative-time-element';
import ProyectoIcon from '../../../../assets/iconsTsx/ProyectoIcon.tsx';
import DefaultImage from '../../../../assets/images/fondoObraPrivada.jpg';

interface ProjectItemCardProps {
  project: {
    id: string | number;
    name: string;
    description?: string;
    zone?: string;
    startDate?: string;
    endDate?: string;
    createdAt?: string;
    proyecto_images?: Array<{ image: string }>;
    subproyecto?: Array<{
      id: string | number;
      name: string;
      description?: string;
      status?: string;
    }>;
  };
  myPerson: boolean;
  onEdit?: (id: string | number) => void;
  onView?: (id: string | number) => void;
  onOpenQuestions?: (id: string | number) => void;
  onManage?: (id: string | number) => void;
  questionsNotAnswered?: number;
  totalQuestions?: number;
}

const ProjectItemCard: React.FC<ProjectItemCardProps> = ({
  project,
  myPerson,
  onEdit,
  onView,
  onOpenQuestions,
  onManage,
  questionsNotAnswered = 0,
  totalQuestions = 0
}) => {
  const coverImage = project?.proyecto_images?.[0]?.image || DefaultImage;
  const buttonLabel = questionsNotAnswered > 0 ? 'Responder preguntas' : 'Preguntas';
  const ownerBadgeValue = questionsNotAnswered > 0 ? questionsNotAnswered : totalQuestions;
  const visitorQuestionsLabel = totalQuestions === 1 ? '1 pregunta' : `${totalQuestions} preguntas`;
  const subProjects = Array.isArray(project.subproyecto) ? project.subproyecto : [];

  const getStatusClass = (status?: string) => {
    if (!status) return '';
    const normalized = status.toLowerCase();
    return styles[`status_${normalized}`] || '';
  };

  const formatDate = (date: string): string => {
    if (!date) return '';
    const utcDate = new Date(date);
    const options = { day: 'numeric' as const, month: 'short' as const };
    return utcDate.toLocaleDateString('es-ES', options);
  };

  return (
    <div className={styles.card}>
      <div className={styles.card_image}>
        <img 
          src={coverImage} 
          alt={project.name}
          className={styles.card_image_img}
        />
      </div>

      <div className={styles.card_header}>
        <div className={styles.card_header_left}>
          <h3 className={styles.card_title}>{project.name}</h3>
          <span className={styles.card_badge}>
            <ProyectoIcon className={styles.card_badge_icon} />
            <span className={styles.card_badge_text}>Proyecto</span>
          </span>
        </div>
        {myPerson && onEdit && (
          <div className={styles.card_header_actions}>
            <button
              className={styles.actionButton}
              onClick={() => onEdit(project.id)}
              title="Editar"
            >
              <Edit size={16} />
            </button>
          </div>
        )}
      </div>

      <div className={styles.card_body}>
        <div className={styles.card_info}>
          <p className={styles.card_description}>
            {project.description || 'Sin descripción'}
          </p>

          <div className={styles.card_details}>
            {project.zone && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Ubicación:</span>
                <span className={styles.detailValue}>{project.zone}</span>
              </div>
            )}

            {(project.startDate || project.endDate) && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Fechas:</span>
                <span className={styles.detailValue}>
                  {formatDate(project.startDate || '')}
                  {project.startDate && project.endDate && ' - '}
                  {formatDate(project.endDate || '')}
                </span>
              </div>
            )}

            {project.createdAt && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Creado:</span>
                <span className={styles.detailValue}>
                  <relative-time lang="es" tense="past" datetime={project.createdAt}></relative-time>
                </span>
              </div>
            )}
          </div>

          {subProjects.length > 0 && (
            <div className={styles.card_subprojects}>
              <div className={styles.subprojects_header}>
                <span className={styles.subprojects_label}>Rubros a cotizar</span>
                <span className={styles.subprojects_count}>
                  {subProjects.length === 1 ? '1 rubro' : `${subProjects.length} rubros`}
                </span>
              </div>
              <div className={styles.subprojects_list}>
                {subProjects.map((subProject) => (
                  <div key={subProject.id} className={styles.subprojects_item}>
                    <div className={styles.subprojects_item_header}>
                    <span className={styles.subprojects_name}>{subProject.name}</span>
                    
                    {subProject.description && (
                      <span className={styles.subprojects_description}>{subProject.description}</span>
                    )}
                    </div>
                    {subProject.status && (
                      <span className={`${styles.subprojects_status} ${getStatusClass(subProject.status)}`}>
                        {subProject.status === 'OPEN' ? 'Abierto' : subProject.status === 'CLOSED' ? 'Cerrado' : 'Finalizado'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.card_footer}>
        {myPerson && onManage && (
          <button
            className={styles.manageButton}
            onClick={() => onManage(project.id)}
          >
            Gestionar proyecto
          </button>
        )}
        {myPerson && onOpenQuestions && (
          <button
            className={styles.questionsButton}
            onClick={() => onOpenQuestions(project.id)}
          >
            <MessageCircle size={16} />
            {buttonLabel}
            {ownerBadgeValue > 0 && (
              <span className={styles.questionsButton_badge}>{ownerBadgeValue}</span>
            )}
          </button>
        )}
        {!myPerson && totalQuestions > 0 && (
          <div className={styles.questionsInfo}>
            <MessageCircle size={16} />
            <span>{visitorQuestionsLabel}</span>
          </div>
        )}
        {onView && (
          <button
            className={styles.viewButton}
            onClick={() => onView(project.id)}
          >
            <Eye size={16} />
            Ver detalles
          </button>
        )}
      </div>
    </div>
  );
};

export default ProjectItemCard;
